import React, { useState, useEffect } from "react";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
import { useProductData } from "../hooks/useProductData";
import { DeleteIcon } from "@/asset/icons";
import { Tooltip } from "antd";

/**
 * Tabbed Product Table Component
 * Displays products in tabs: Food, Platter, Non-edibles
 */
const TabbedProductTable = ({
  activeTab = 0,
  onTabChange,
  onAddProduct,
  onEditProduct,
  onViewProduct,
  onDeleteProduct,
  onBulkPurchase,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Use custom hook for product data
  const {
    foodProducts,
    platterProducts,
    nonEdiblesProducts,
    totalFood,
    totalPlatter,
    totalNonEdibles,
    categoryIds,
    isLoading,
    isFetching,
    error,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,
    search,
    handleSearch,
  } = useProductData(activeTab);

  // Clear selection when switching tabs
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [activeTab]);

  // Get current tab's data - this ensures we always show the right data
  const getCurrentTabData = () => {
    switch (activeTab) {
      case 0: return foodProducts;
      case 1: return platterProducts;
      case 2: return nonEdiblesProducts;
      default: return [];
    }
  };

  const currentTabData = getCurrentTabData();

  // Debug: Log what the table component receives
  console.log("📋 TabbedProductTable Debug:", {
    activeTab,
    currentTabData: currentTabData?.length,
    isLoading,
    isFetching,
    foodProducts: foodProducts?.length,
    platterProducts: platterProducts?.length,
    nonEdiblesProducts: nonEdiblesProducts?.length,
    totalFood,
    totalPlatter,
    totalNonEdibles,
    categoryIds: {
      food: categoryIds.food,
      platter: categoryIds.platter,
      nonEdibles: categoryIds.nonEdibles,
    },
    platterCategoryExists: !!categoryIds.platter,
    nonEdiblesCategoryExists: !!categoryIds.nonEdibles,
  });

  // Define columns for product data (matching screenshot structure)
  const productColumns = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text, record) => (
        <div className="flex items-center gap-3">
          <div className="relative">
            {console.log("📋 Product Image Debug:", record.image?.url)}
            {record.image && Object.keys(record.image).length > 0 ? (
              <img
                src={record.image.url}
                alt={text}
                className="w-10 h-10 rounded-full object-cover border border-gray-200"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : (
              <div
                className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center"
                style={{
                  display:
                    record.images && record.images.length > 0 ? "none" : "flex",
                }}
              >
                <span className="text-gray-400 text-xs">📦</span>
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">{text}</span>
            <span className="text-sm text-gray-500">SFS{record.id}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 150,
      render: (category) => (
        <span className="text-gray-600">{category?.name || "N/A"}</span>
      ),
    },
    {
      title: "Stock",
      dataIndex: "current_qty",
      key: "current_qty",
      width: 100,
      render: (qty) => <span className="font-medium text-gray-900">{qty}</span>,
    },
    {
      title: "Location",
      dataIndex: "locations",
      key: "locations",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (locations) => {
        if (!locations || locations.length === 0) {
          return <span className="text-gray-600">N/A</span>;
        }
        
        const locationNames = locations.map(loc => loc.name).join(', ');
        
        return (
          <Tooltip title={locationNames} placement="topLeft">
            <span className="text-gray-600 block truncate">
              {locationNames}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Price",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (amount) => (
        <span className="font-medium text-gray-900">
          ₦{parseFloat(amount).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Is Add-on",
      dataIndex: "can_be_addon",
      key: "can_be_addon",
      width: 100,
      render: (canBeAddon) => (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            canBeAddon === 1 || canBeAddon === true
              ? "bg-blue-100 text-blue-700"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {canBeAddon === 1 || canBeAddon === true ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            status === 1
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full mr-1 ${
              status === 1 ? "bg-green-500" : "bg-red-500"
            }`}
          ></span>
          {status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  // Get action button based on active tab
  const getActionButton = () => {
    const buttonLabels = {
      0: "Add Product", // Products tab - keep for button display
      1: "Add Platter", // Platter tab - keep for button display
      2: "Add Non-edibles", // Non-edibles tab - keep for button display
    };

    const productTypeLabels = {
      0: "Product", // For modal footer
      1: "Platter", // For modal footer
      2: "Non-edibles", // For modal footer
    };

    // Get the appropriate category ID based on active tab
    const getCategoryId = () => {
      switch (activeTab) {
        case 1:
          return categoryIds.platter; // Platter tab
        case 2:
          return categoryIds.nonEdibles; // Non-edibles tab
        default:
          return null; // Products tab - no specific category
      }
    };

    // Check if category exists for current tab
    const categoryExists = () => {
      switch (activeTab) {
        case 1:
          return !!categoryIds.platter;
        case 2:
          return !!categoryIds.nonEdibles;
        default:
          return true; // Products tab always enabled
      }
    };

    // Get tooltip message for disabled button
    const getTooltipMessage = () => {
      switch (activeTab) {
        case 1:
          return "Please create a 'Platter' category first before adding platter products";
        case 2:
          return "Please create a 'Non-Edibles' category first before adding non-edible products";
        default:
          return "";
      }
    };

    const isDisabled = !categoryExists();

    return [
      {
        label: buttonLabels[activeTab] || "Add Product",
        type: isDisabled ? "default" : "primary",
        icon: <PlusOutlined />,
        onClick: () => {
          if (!isDisabled) {
            onAddProduct &&
              onAddProduct({
                productType: productTypeLabels[activeTab] || "Product", // Use productTypeLabels
                categoryId: getCategoryId(),
              });
          }
        },
        className: isDisabled 
          ? "" 
          : "bg-orange-500 border-orange-500 hover:bg-orange-600",
        disabled: isDisabled,
        tooltip: isDisabled ? getTooltipMessage() : undefined,
        // Inline styles to enforce grey color for disabled state
        style: isDisabled 
          ? {
              backgroundColor: '#d1d5db',
              borderColor: '#d1d5db',
              color: '#6b7280',
              cursor: 'not-allowed',
              opacity: 0.6,
              pointerEvents: 'none',
            }
          : undefined,
      },
    ];
  };

  const actionButtons = getActionButton();

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // Row actions for product table
  const rowActions = [
    {
      label: "View Details",
      icon: <EyeOutlined />,
      onClick: (record) => onViewProduct && onViewProduct(record),
    },
    {
      label: "Edit Product",
      icon: <EditOutlined />,
      onClick: (record) => onEditProduct && onEditProduct(record),
    },
    {
      label: "Delete Product",
      icon: <DeleteIcon />,
      onClick: (record) => onDeleteProduct && onDeleteProduct(record),
    },
  ];

  // Handle bulk actions
  const handleBulkPurchase = (selectedKeys) => {
    console.log("Bulk purchase products:", selectedKeys);
    // Implement bulk purchase logic here
  };

  // Define tabs configuration with counts in labels (matching screenshot)
  const tabs = [
    {
      key: 0,
      label: `Products`,
      data: foodProducts,
      count: totalFood,
    },
    {
      key: 1,
      label: `Platter`,
      data: platterProducts,
      count: totalPlatter,
    },
    {
      key: 2,
      label: `Non-Edibles(e.g. Packs)`,
      data: nonEdiblesProducts,
      count: totalNonEdibles,
    },
  ];

  console.log("📋 Tabs Configuration Debug:", {
    tabs,
    currentTabData: tabs[activeTab],
    currentTabLabel: tabs[activeTab]?.label,
    dataBeingPassed: currentTabData?.length,
  });

  return (
    <CustomDataTable
      title="Bulk Purchase Products"
      data={currentTabData} // Use currentTabData instead of tabs[activeTab].data
      columns={productColumns}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      showSearch={true}
      searchPlaceholder="Search products..."
      searchValue={search}
      onServerSearch={handleSearch}
      searchMode="server"
      actionButtons={actionButtons}
      // showFilter={true}
      showCSV={true}
      paginationMode="server"
      currentPage={paginationInfo.current_page}
      totalItems={paginationInfo.total}
      serverPageSize={paginationInfo.per_page}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      rowSelection={rowSelection}
      selectedRowKeys={selectedRowKeys}
      rowActions={rowActions}
      loading={isLoading || isFetching}
      footerInfo={{
        left: `${selectedRowKeys.length} of ${currentTabData?.length || 0} row(s) selected.`,
      }}
    />
  );
};

export default TabbedProductTable;